import i18n from '@/utils/i18n';
import { getTranslationValue } from '@/utils/translations';
import { OrderSummaryInputID, PoNumberDisplay, ViewableBy } from '@/enums';
import { useCallback, useMemo, useState } from 'react';
import type { InputValueMap } from '@/utils/new-order';
import type {
  OrderSummaryConfig,
  OrderSummaryInput,
} from '@domain/configuration/types';

export type AdditionalDetailsErrorMap = Record<string, string | undefined>;

/**
 * Creates the default PO Number input configuration.
 * @param required - Whether the field is required (based on poNumberDisplayMode setting)
 */
const createPoNumberInputConfig = (required: boolean): OrderSummaryInput => ({
  id: OrderSummaryInputID.PONumber,
  pattern: null,
  pattern_invalid_message: null,
  placeholder: null,
  required,
  subtitle: null,
  title: i18n.t('po_number'),
  type: 'text',
  viewable_by: ViewableBy.ALL,
});

/**
 * Creates the default Order Notes input configuration.
 */
const createOrderNotesInputConfig = (): OrderSummaryInput => ({
  id: OrderSummaryInputID.Notes,
  pattern: null,
  pattern_invalid_message: null,
  placeholder: null,
  required: false,
  subtitle: i18n.t('order_notes_description'),
  title: i18n.t('order_notes'),
  type: 'textarea',
  viewable_by: ViewableBy.ALL,
});

const validateCheckoutPanelAdditionalDetails = (
  inputs: OrderSummaryInput[],
  valueMap: InputValueMap,
): { errorMap: AdditionalDetailsErrorMap; isValid: boolean } => {
  if (inputs.length === 0) {
    return { errorMap: {}, isValid: true };
  }

  const nextErrorMap: Record<string, string> = {};
  let hasError = false;

  for (const input of inputs) {
    const inputValue = valueMap[input.id]?.value ?? '';
    const trimmedValue = inputValue.trim();

    if (input.required && !trimmedValue) {
      hasError = true;
      nextErrorMap[input.id] = i18n.t('field_is_required');
      continue;
    }

    if (
      input.pattern &&
      trimmedValue &&
      !new RegExp(input.pattern).test(trimmedValue)
    ) {
      hasError = true;
      nextErrorMap[input.id] = input.pattern_invalid_message
        ? getTranslationValue(input.pattern_invalid_message)
        : i18n.t('field_is_invalid');
    }
  }

  return {
    errorMap: nextErrorMap,
    isValid: !hasError,
  };
};

/**
 * Hook for managing checkout panel additional input fields (PO number, order notes, custom fields).
 *
 * Builds the list of additional inputs based on:
 * - Supplier metadata settings (poNumberDisplayMode, showOrderNotes)
 * - Custom order summary config (additional_inputs)
 *
 * Also provides validation functions for required fields and regex patterns.
 *
 * @param orderSummaryConfig - Optional custom config with additional_inputs
 * @param poNumberDisplayMode - Display mode for PO number field (Hide, Optional, Required)
 * @param showOrderNotes - Whether to show order notes field
 * @param valueMap - Current values for all input fields
 *
 * @returns Object containing:
 * - additionalInputs: Array of input field configurations (filtered for supplier visibility)
 * - validateAll: Validates all fields and returns isValid boolean
 * - validateField: Validates a single field by ID
 * - clearFieldError: Clears error for a single field
 * - clearAllErrors: Clears all field errors
 * - errorMap: Current error state for all fields
 */
export function useSalesPortalCheckoutAdditionalInputs(
  orderSummaryConfig: OrderSummaryConfig | null | undefined,
  poNumberDisplayMode: PoNumberDisplay,
  showOrderNotes: boolean,
  valueMap: InputValueMap,
) {
  const additionalInputs = useMemo(() => {
    const defaultInputsConfig: OrderSummaryInput[] = [
      poNumberDisplayMode !== PoNumberDisplay.Hide
        ? createPoNumberInputConfig(
            poNumberDisplayMode === PoNumberDisplay.Required,
          )
        : null,
      showOrderNotes ? createOrderNotesInputConfig() : null,
    ].filter((input) => input !== null);

    // Additional inputs could be empty so we want to default to the default inputs
    // since po number and order notes are separate requirements
    const configuredInputs = !!orderSummaryConfig?.additional_inputs?.length
      ? orderSummaryConfig.additional_inputs
      : defaultInputsConfig;

    // Filter out restaurant-only fields (Sales Portal is supplier-facing)
    return configuredInputs.filter(
      (input) => input.viewable_by !== ViewableBy.RESTAURANT,
    );
  }, [
    orderSummaryConfig?.additional_inputs,
    poNumberDisplayMode,
    showOrderNotes,
  ]);

  const [additionalInputErrorMap, setAdditionalInputErrorMap] =
    useState<AdditionalDetailsErrorMap>({});

  const validateAdditionalInput = useCallback(
    (id: string): boolean => {
      const targetInput = additionalInputs.find((input) => input.id === id);
      if (!targetInput) {
        return true;
      }

      const { errorMap: nextErrorMap, isValid } =
        validateCheckoutPanelAdditionalDetails([targetInput], valueMap);

      setAdditionalInputErrorMap((previous) => ({
        ...previous,
        [id]: nextErrorMap[id],
      }));

      return isValid;
    },
    [additionalInputs, valueMap],
  );

  // Pure validity check that doesn't cause state updates - safe to call during render
  const areAdditionalInputsValid = useMemo((): boolean => {
    const { isValid } = validateCheckoutPanelAdditionalDetails(
      additionalInputs,
      valueMap,
    );
    return isValid;
  }, [additionalInputs, valueMap]);

  return useMemo(
    () => ({
      additionalInputErrorMap,
      additionalInputs,
      areAdditionalInputsValid,
      clearAdditionalInputError: (id: string) => {
        setAdditionalInputErrorMap((previous) => {
          if (!previous[id]) {
            return previous;
          }
          return { ...previous, [id]: undefined };
        });
      },
      clearAllAdditionalInputError: () => {
        setAdditionalInputErrorMap({});
      },
      validateAdditionalInput,
    }),
    [
      additionalInputErrorMap,
      additionalInputs,
      areAdditionalInputsValid,
      validateAdditionalInput,
    ],
  );
}
